import React, { useCallback, useMemo } from 'react';
import { type TextElement } from '@bynder-studio/render-core';
import { ExtendedColorParams } from '@bynder-studio/misc';
import { useTranslate } from '@bynder/localization';
import ShotColor from 'packages/pages/design/sidebar/shots/ShotColor';
import { flattenTree } from '~/common/editor/helpers/elementtree';
import useElement from '../../hooks/useElement';
import { usePalettes } from '../../hooks/usePalettes';
import { collectConfigureColors, getUnifiedColor } from '../ShapeElement/utils'; // todo: move to a shared location

type Props = {
    elementId: number;
    template: any;
    selectedPageIndex: number;
};

const BackgroundBorderColor = ({ elementId, template, selectedPageIndex }: Props) => {
    const { translate } = useTranslate();
    const { updateElement, element } = useElement<TextElement>(elementId);
    const { byId } = usePalettes();

    const selectedColor = useMemo(
        () => (element?.textBackground?.borderColor ? getUnifiedColor(element.textBackground.borderColor, byId) : null),
        [element?.textBackground?.borderColor, byId],
    );

    const colors = useMemo(() => {
        const templateElements = flattenTree(template.pages[selectedPageIndex].elements);
        const templateElement = templateElements[elementId];

        if (
            !templateElement ||
            !templateElement.properties.textBackground ||
            !templateElement.properties.textBackground.borderColor ||
            !templateElement.properties.textBackground.borderBrandColors
        ) {
            return [];
        }

        return collectConfigureColors(
            templateElement,
            'textBackground.borderBrandColors',
            'textBackground.borderColor',
            byId,
        );
    }, [template, selectedPageIndex, elementId, byId]);

    const handleColorClick = useCallback(
        ({ name, ...color }: ExtendedColorParams) => {
            if (!element) {
                return;
            }

            updateElement({ textBackground: { ...element.textBackground, borderColor: color } });
        },
        [updateElement, element],
    );

    if (!element || !selectedColor || colors.length < 2) {
        return null;
    }

    return (
        <ShotColor
            title={translate('editor.sidebar.shots.text.background.border_color.title')}
            elementId={elementId}
            selectedColor={selectedColor}
            colors={colors}
            isDisabled={false}
            handleColorClick={handleColorClick}
        />
    );
};

export default BackgroundBorderColor;
